import { z } from 'zod';

export const WORKPLACE_TYPES = ['ONSITE', 'HYBRID', 'REMOTE'] as const;
export const workplaceTypeSchema = z.enum(WORKPLACE_TYPES);
export type WorkplaceType = z.infer<typeof workplaceTypeSchema>;

export const EMPLOYMENT_TYPES = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'INTERNSHIP', 'TEMPORARY'] as const;
export const employmentTypeSchema = z.enum(EMPLOYMENT_TYPES);
export type EmploymentType = z.infer<typeof employmentTypeSchema>;

export const JOB_STATUSES = ['DRAFT', 'PUBLISHED', 'CLOSED', 'ARCHIVED'] as const;
export const jobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof jobStatusSchema>;

export const APPLICATION_STATUSES = [
  'NEW',
  'IN_REVIEW',
  'INTERVIEW',
  'OFFER',
  'HIRED',
  'REJECTED',
  'WITHDRAWN',
] as const;
export const applicationStatusSchema = z.enum(APPLICATION_STATUSES);
export type ApplicationStatus = z.infer<typeof applicationStatusSchema>;

export const CONTACT_MESSAGE_STATUSES = ['NEW', 'IN_PROGRESS', 'REPLIED', 'ARCHIVED'] as const;
export const contactMessageStatusSchema = z.enum(CONTACT_MESSAGE_STATUSES);
export type ContactMessageStatus = z.infer<typeof contactMessageStatusSchema>;

export const QUESTION_TYPES = [
  'SHORT_TEXT',
  'LONG_TEXT',
  'BOOLEAN',
  'SINGLE_SELECT',
  'MULTI_SELECT',
  'NUMBER',
  'URL',
  'DATE',
] as const;
export const questionTypeSchema = z.enum(QUESTION_TYPES);
export type QuestionType = z.infer<typeof questionTypeSchema>;

/** Question types whose answers are picked from a fixed list of choices. */
export const CHOICE_QUESTION_TYPES = [
  'SINGLE_SELECT',
  'MULTI_SELECT',
] as const satisfies readonly QuestionType[];

export function isChoiceQuestionType(type: QuestionType): boolean {
  return (CHOICE_QUESTION_TYPES as readonly QuestionType[]).includes(type);
}

export const COMPENSATION_PERIODS = ['HOUR', 'DAY', 'WEEK', 'MONTH', 'YEAR'] as const;
export const compensationPeriodSchema = z.enum(COMPENSATION_PERIODS);
export type CompensationPeriod = z.infer<typeof compensationPeriodSchema>;

export const ADMIN_ROLES = ['ADMIN'] as const;
export const adminRoleSchema = z.enum(ADMIN_ROLES);
export type AdminRole = z.infer<typeof adminRoleSchema>;

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  SHORT_TEXT: 'Short text',
  LONG_TEXT: 'Long text',
  BOOLEAN: 'Yes / no',
  SINGLE_SELECT: 'Single select',
  MULTI_SELECT: 'Multi-select',
  NUMBER: 'Number',
  URL: 'URL',
  DATE: 'Date',
};

export const WORKPLACE_TYPE_LABELS: Record<WorkplaceType, string> = {
  ONSITE: 'On-site',
  HYBRID: 'Hybrid',
  REMOTE: 'Remote',
};

export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = {
  FULL_TIME: 'Full-time',
  PART_TIME: 'Part-time',
  CONTRACT: 'Contract',
  INTERNSHIP: 'Internship',
  TEMPORARY: 'Temporary',
};

export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  CLOSED: 'Closed',
  ARCHIVED: 'Archived',
};

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  NEW: 'New',
  IN_REVIEW: 'In review',
  INTERVIEW: 'Interview',
  OFFER: 'Offer',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
};

export const CONTACT_MESSAGE_STATUS_LABELS: Record<ContactMessageStatus, string> = {
  NEW: 'New',
  IN_PROGRESS: 'In progress',
  REPLIED: 'Replied',
  ARCHIVED: 'Archived',
};

export const COMPENSATION_PERIOD_LABELS: Record<CompensationPeriod, string> = {
  HOUR: 'per hour',
  DAY: 'per day',
  WEEK: 'per week',
  MONTH: 'per month',
  YEAR: 'per year',
};
